/**
 * Bitrix API URL utilities
 *
 * Converts REST-like API paths to Bitrix flat file structure.
 * Example: '/api/popular-queries/increment' -> 'popular_queries_increment.php'
 */

/**
 * Check if running in Bitrix mode
 */
export function isBitrixMode(): boolean {
  return typeof window !== 'undefined' &&
         window.LiveSearchConfig?.bitrixMode === true;
}

/**
 * Get Bitrix API base path (without trailing slash)
 */
export function getBitrixApiBasePath(): string {
  if (typeof window === 'undefined') {
    // SSR case - return default Bitrix path
    return '/local/modules/live_search/api';
  }

  const config = window.LiveSearchConfig;
  if (!config?.apiBasePath) {
    // Default Bitrix path for production bundle
    return '/local/modules/live_search/api';
  }

  // Ensure no trailing slash
  let basePath = config.apiBasePath;
  if (basePath.endsWith('/')) {
    basePath = basePath.slice(0, -1);
  }
  return basePath;
}

/**
 * Convert REST API path to Bitrix flat filename
 *
 * @param path REST path like '/api/popular-queries/increment'
 * @returns Filename like 'popular_queries_increment.php'
 */
export function restPathToBitrixFile(path: string): string {
  // Remove leading '/api/' if present
  let normalized = path.replace(/^\/api\//, '');

  // Replace all slashes with underscores
  normalized = normalized.replace(/\//g, '_');

  // Replace hyphens with underscores
  normalized = normalized.replace(/-/g, '_');

  // Add .php extension
  return normalized + '.php';
}

/**
 * Build full Bitrix API URL
 *
 * @param path REST API path
 * @returns Full URL for Bitrix environment
 */
export function buildBitrixApiUrl(path: string): string {
  if (!isBitrixMode()) {
    // In non-bitrix mode, return path as-is (relative or absolute)
    return path;
  }

  const basePath = getBitrixApiBasePath();
  const filename = restPathToBitrixFile(path);

  return `${basePath}/${filename}`;
}

/**
 * Build popular queries endpoint URL
 */
export function buildPopularQueriesUrl(): string {
  return buildBitrixApiUrl('/api/popular-queries');
}

/**
 * Build popular queries increment endpoint URL
 */
export function buildPopularQueriesIncrementUrl(): string {
  return buildBitrixApiUrl('/api/popular-queries/increment');
}

/**
 * Build search endpoint URL
 */
export function buildSearchUrl(): string {
  return buildBitrixApiUrl('/api/search');
}

/**
 * Build click endpoint URL
 */
export function buildClickUrl(): string {
  return buildBitrixApiUrl('/api/click');
}

/**
 * Build health endpoint URL
 */
export function buildHealthUrl(): string {
  return buildBitrixApiUrl('/api/health');
}

/**
 * Build cart add endpoint URL
 */
export function buildCartAddUrl(): string {
  return buildBitrixApiUrl('/api/cart/add');
}